import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight, Sparkles, Quote, Instagram } from "lucide-react";
import { api, formatTL } from "@/lib/api";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const { settings } = useOutletContext() || {};
  const [home, setHome] = useState(null);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/homepage"),
      api.get("/products", { params: { featured: true, limit: 8 } }),
    ]).then(([h, p]) => { setHome(h.data); setFeatured(p.data); })
      .finally(() => setLoading(false));
  }, []);

  const currency = settings?.currency_symbol || "₺";

  if (loading || !home) {
    return <div className="max-w-7xl mx-auto px-6 py-32 text-center text-larc-muted">Yükleniyor…</div>;
  }

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-larc-alt">
        {home.hero_image && (
          <img src={home.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-80" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-larc-bg/30 via-larc-bg/10 to-larc-bg" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 text-center animate-fade-up">
          <div className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-larc-ink/80 bg-white/60 backdrop-blur px-4 py-1.5 rounded-full">
            <Sparkles size={12} className="text-larc-gold" />
            küçük parti · el yapımı
          </div>
          <h1 className="font-heading text-5xl md:text-7xl tracking-tight leading-[1.05] mt-8 text-larc-ink">
            {home.hero_title}
          </h1>
          <p className="mt-6 text-lg text-larc-muted font-light max-w-xl mx-auto leading-relaxed">
            {home.hero_subtitle}
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Link to="/urunler" className="inline-flex items-center gap-2 bg-larc-gold text-white rounded-full px-8 py-3.5 text-sm tracking-wide hover:bg-larc-gold-hover transition-colors active:scale-95" data-testid="hero-cta">
              {home.hero_button_text || "Koleksiyonu Keşfet"} <ArrowRight size={16} />
            </Link>
            <Link to="/kisiye-ozel" className="inline-flex items-center gap-2 border border-larc-border bg-white/70 backdrop-blur rounded-full px-8 py-3.5 text-sm hover:border-larc-gold transition-colors">
              Kişiye Özel Üretim
            </Link>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="featured-section">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-14">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">— Koleksiyon</div>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight mt-3">{home.featured_title}</h2>
            <p className="text-larc-muted mt-2">{home.featured_subtitle}</p>
          </div>
          <Link to="/urunler" className="text-sm text-larc-ink hover:text-larc-gold inline-flex items-center gap-1.5">Tümünü Gör <ArrowRight size={14} /></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {featured.slice(0, 8).map((p) => <ProductCard key={p.id} product={p} currency={currency} />)}
        </div>
      </section>

      {/* Categories split */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid md:grid-cols-2 gap-6 md:gap-10">
        <CategoryTile to="/urunler/mumlar" title={home.candle_title} subtitle={home.candle_subtitle} image={home.candle_image} testid="candle-tile" />
        <CategoryTile to="/urunler/sabunlar" title={home.soap_title} subtitle={home.soap_subtitle} image={home.soap_image} testid="soap-tile" />
      </section>

      {/* Custom order */}
      <section className="bg-larc-alt" data-testid="custom-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
          <div>
            <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">— Atölyemiz</div>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight mt-3">{home.custom_title}</h2>
            <p className="text-larc-muted mt-4 leading-relaxed max-w-md">{home.custom_subtitle}</p>
            <Link to="/kisiye-ozel" className="mt-8 inline-flex items-center gap-2 bg-larc-ink text-white rounded-full px-8 py-3.5 text-sm hover:bg-larc-gold transition-colors">
              Kişiye Özel Sipariş <ArrowRight size={16} />
            </Link>
          </div>
          <div className="relative aspect-[4/5] md:aspect-square rounded-2xl overflow-hidden">
            <img src={home.custom_image} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32 grid md:grid-cols-2 gap-12 md:gap-20 items-center" data-testid="story-section">
        <div className="relative aspect-[4/5] rounded-2xl overflow-hidden order-2 md:order-1">
          <img src={home.story_image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="order-1 md:order-2">
          <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">— Hikâyemiz</div>
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight mt-3">{home.story_title}</h2>
          <p className="text-larc-muted mt-5 leading-relaxed">{home.story_text}</p>
          <Link to="/hikayemiz" className="mt-6 inline-flex items-center gap-1.5 text-sm text-larc-ink hover:text-larc-gold">Daha fazla oku <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* Testimonials */}
      {home.testimonials?.length > 0 && (
        <section className="bg-larc-alt" data-testid="testimonials-section">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
            <div className="text-center mb-14">
              <div className="text-xs tracking-[0.25em] uppercase text-larc-gold">— Müşterilerimizden</div>
              <h2 className="font-heading text-4xl md:text-5xl mt-3">Sevgiyle anılan kelimeler</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {home.testimonials.map((t, i) => (
                <div key={i} className="bg-white rounded-2xl border border-larc-border p-8 shadow-[0_8px_30px_rgba(45,38,34,0.04)]">
                  <Quote size={22} className="text-larc-gold" />
                  <p className="mt-5 text-larc-ink leading-relaxed font-light">"{t.text}"</p>
                  <div className="mt-6 text-sm text-larc-muted">— {t.name}, {t.city}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instagram grid */}
      {home.instagram_images?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="instagram-section">
          <div className="text-center mb-12">
            <div className="text-xs tracking-[0.25em] uppercase text-larc-gold inline-flex items-center gap-2"><Instagram size={12} />— Atölyeden anlar</div>
            <h2 className="font-heading text-4xl md:text-5xl mt-3">{settings?.logo_text ? `@${settings.logo_text.toLowerCase()}.shop` : "@larcraft.shop"}</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 md:gap-4">
            {home.instagram_images.slice(0, 6).map((src, i) => (
              <a key={i} href={settings?.instagram_url || "#"} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-xl bg-larc-alt group">
                <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </a>
            ))}
          </div>
        </section>
      )}

      {/* CTA / newsletter */}
      <section className="max-w-5xl mx-auto px-6 md:px-10 py-24" data-testid="cta-section">
        <div className="rounded-3xl bg-larc-ink text-larc-bg px-8 md:px-16 py-16 text-center relative overflow-hidden">
          <div className="relative z-10">
            <h2 className="font-heading text-4xl md:text-5xl">{home.cta_title}</h2>
            <p className="text-larc-bg/70 mt-4 max-w-lg mx-auto">{home.cta_subtitle}</p>
            <Link to="/iletisim" className="mt-8 inline-flex items-center gap-2 bg-larc-gold text-white rounded-full px-8 py-3.5 text-sm hover:bg-larc-gold-hover transition-colors">
              Bizimle iletişime geç <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function CategoryTile({ to, title, subtitle, image, testid }) {
  return (
    <Link to={to} className="group relative overflow-hidden rounded-2xl bg-larc-alt aspect-[4/3] md:aspect-[5/6] block" data-testid={testid}>
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-larc-ink/60 via-larc-ink/10 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 text-white">
        <h3 className="font-heading text-3xl md:text-5xl">{title}</h3>
        <p className="text-white/80 mt-2 text-sm">{subtitle}</p>
        <span className="inline-flex items-center gap-1.5 mt-4 text-sm">Keşfet <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" /></span>
      </div>
    </Link>
  );
}
